import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, X, ArrowRight, Terminal } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SECTORS } from "@/lib/site";
import { AppHeader } from "@/components/AppHeader";

const LANGUAGES = [
  { label: "Türkçe", market: "Türkiye" },
  { label: "İngilizce", market: "İngilizce konuşulan uluslararası pazarlar (ABD, İngiltere)" },
  { label: "Arapça", market: "Körfez ve Arap pazarı" },
  { label: "Almanca", market: "Almanya ve Avusturya" },
];

export default function NewReport() {
  const navigate = useNavigate();
  const [brand, setBrand] = useState("");
  const [category, setCategory] = useState("");
  const [sector, setSector] = useState("");
  const [city, setCity] = useState("");
  const [langs, setLangs] = useState(["İngilizce"]);
  const [competitors, setCompetitors] = useState([""]);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMsg, setProgressMsg] = useState("");
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const toggleLang = (l) =>
    setLangs((p) => (p.includes(l) ? p.filter((x) => x !== l) : [...p, l]));

  const updateComp = (i, v) => setCompetitors((p) => p.map((c, idx) => (idx === i ? v : c)));
  const addComp = () => competitors.length < 5 && setCompetitors((p) => [...p, ""]);
  const removeComp = (i) => setCompetitors((p) => p.filter((_, idx) => idx !== i));

  const poll = (id) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/reports/${id}/status`);
        setProgress(res.data.progress || 0);
        setProgressMsg(res.data.progress_msg || "");
        if (res.data.status === "done") {
          clearInterval(pollRef.current);
          navigate(`/rapor/${id}`);
        } else if (res.data.status === "failed") {
          clearInterval(pollRef.current);
          setSubmitting(false);
          toast.error("Rapor oluşturulamadı. Lütfen tekrar deneyin.");
        }
      } catch {
        clearInterval(pollRef.current);
        setSubmitting(false);
        toast.error("Bağlantı hatası.");
      }
    }, 2500);
  };

  const submit = async () => {
    if (!brand.trim() || !category.trim() || !sector || langs.length === 0) {
      toast.error("Marka adı, kategori, sektör ve en az bir dil gerekli.");
      return;
    }
    setSubmitting(true);
    setProgress(2);
    setProgressMsg("Başlatılıyor…");
    try {
      const markets = LANGUAGES.filter((l) => langs.includes(l.label)).map((l) => l.market);
      const res = await api.post("/reports", {
        brand_name: brand.trim(),
        category: category.trim(),
        sector,
        city: city.trim(),
        target_markets: markets,
        languages: langs,
        competitors: competitors.map((c) => c.trim()).filter(Boolean),
      });
      poll(res.data.id);
    } catch (e) {
      setSubmitting(false);
      toast.error(e.response?.data?.detail || "Rapor oluşturulamadı.");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <p className="font-mono text-xs tracking-[0.2em] uppercase text-brand mb-2">Yeni Rapor</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-8">
          Markanı analiz edelim
        </h1>

        <div className="space-y-8">
          <Field label="Marka adı" hint="Kliniğinin / markanın tam adı">
            <input data-testid="input-brand" value={brand} onChange={(e) => setBrand(e.target.value)}
              placeholder="Örn. Vera Clinic"
              className="w-full border border-input bg-background px-4 py-3 outline-none focus:border-brand transition-colors" />
          </Field>

          <Field label="Kategori / sektör" hint="Müşterilerin aradığı hizmet">
            <input data-testid="input-category" value={category} onChange={(e) => setCategory(e.target.value)}
              placeholder="Örn. saç ekimi kliniği"
              className="w-full border border-input bg-background px-4 py-3 outline-none focus:border-brand transition-colors" />
          </Field>

          <Field label="Sektör" hint="İşletmenizin ana sektörü">
            <select data-testid="input-sector" value={sector} onChange={(e) => setSector(e.target.value)}
              className="w-full border border-input bg-background px-4 py-3 outline-none focus:border-brand transition-colors">
              <option value="">Sektör seçin</option>
              {SECTORS.map((s) => <option key={s} value={s}>{s}</option>)}
              <option value="Diğer">Diğer</option>
            </select>
          </Field>

          <Field label="Şehir / bölge" hint="Sorular bu konuma göre üretilir (opsiyonel)">
            <input data-testid="input-city" value={city} onChange={(e) => setCity(e.target.value)}
              placeholder="Örn. İstanbul"
              className="w-full border border-input bg-background px-4 py-3 outline-none focus:border-brand transition-colors" />
          </Field>

          <Field label="Hedef pazar & dil" hint="Birden fazla seçebilirsin">
            <div className="flex flex-wrap gap-2">
              {LANGUAGES.map((l) => {
                const active = langs.includes(l.label);
                return (
                  <button key={l.label} data-testid={`lang-${l.label}`} onClick={() => toggleLang(l.label)}
                    className={`px-4 py-2 text-sm font-medium border transition-colors ${active ? "bg-brand text-white border-brand" : "border-input hover:border-brand"}`}>
                    {l.label}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label="Rakipler" hint="En fazla 5 rakip marka ekle (opsiyonel)">
            <div className="space-y-2">
              {competitors.map((c, i) => (
                <div key={i} className="flex gap-2">
                  <input data-testid={`input-competitor-${i}`} value={c} onChange={(e) => updateComp(i, e.target.value)}
                    placeholder={`Rakip ${i + 1}`}
                    className="flex-1 border border-input bg-background px-4 py-2.5 outline-none focus:border-brand transition-colors" />
                  {competitors.length > 1 && (
                    <button onClick={() => removeComp(i)} className="px-3 border border-input text-muted-foreground hover:text-destructive">
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
              {competitors.length < 5 && (
                <button data-testid="add-competitor-btn" onClick={addComp}
                  className="flex items-center gap-1.5 text-sm text-brand font-medium mt-1">
                  <Plus size={16} weight="bold" /> Rakip ekle
                </button>
              )}
            </div>
          </Field>

          <button data-testid="new-report-submit" onClick={submit} disabled={submitting}
            className="w-full flex items-center justify-center gap-2 bg-brand text-white font-medium py-4 transition-transform hover:-translate-y-0.5 disabled:opacity-60">
            Raporu Oluştur <ArrowRight size={20} weight="bold" />
          </button>
          <p className="text-xs text-muted-foreground text-center">
            10 soru, her biri 3 AI'ya birden çok kez sorulur. Bu birkaç dakika sürebilir.
          </p>
        </div>
      </main>

      {submitting && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="progress-overlay">
          <div className="bg-foreground text-background w-full max-w-lg p-8 font-mono">
            <div className="flex items-center gap-2 text-score-good mb-6">
              <Terminal size={18} weight="bold" />
              <span className="text-sm">ai-gorunurluk@engine</span>
            </div>
            <p className="text-lg mb-2">{progressMsg || "Sorular AI'lara soruluyor…"}</p>
            <div className="h-2 bg-white/10 mt-6">
              <div className="h-full bg-score-good transition-all duration-700" style={{ width: `${progress}%` }} />
            </div>
            <p className="text-right text-sm mt-2 tabular-nums text-score-good">{progress}%</p>
            <p className="text-xs text-white/40 mt-6 leading-relaxed">
              ChatGPT, Perplexity ve Gemini'ye gerçek hasta sorularını soruyoruz. Cevaplar
              değişkenlik gösterir; bu yüzden her soruyu birden çok kez çalıştırıyoruz.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, hint, children }) => (
  <div>
    <label className="font-mono text-xs font-bold tracking-[0.15em] uppercase">{label}</label>
    {hint && <p className="text-xs text-muted-foreground mt-0.5 mb-2.5">{hint}</p>}
    {children}
  </div>
);
